import { useMemo } from 'react'
import { AnyActionButton, DropdownMenuItem } from '../types'

export interface CardActionConfig {
  key: string
  label: string
  icon?: React.ReactNode
  type?: 'default' | 'primary' | 'dashed' | 'link' | 'text' | 'dropdown'
  danger?: boolean
  disabled?: boolean
  onClick?: () => void
  loading?: boolean
  isPrimary?: boolean
  condition?: boolean
  order?: number
  menuItems?: DropdownMenuItem[]
}

export interface UseCardActionsProps {
  actions: CardActionConfig[]
}

export interface UseCardActionsReturn {
  primaryAction?: AnyActionButton
  secondaryActions: AnyActionButton[]
}

const createActionButton = (config: CardActionConfig, defaultType: string): AnyActionButton => {
  const baseAction = {
    key: config.key,
    label: config.label,
    danger: config.danger,
    disabled: config.disabled,
    loading: config.loading,
    icon: config.icon,
  }

  if (config.type === 'dropdown') {
    return {
      ...baseAction,
      type: 'dropdown' as const,
      menuItems: config.menuItems || [],
    }
  }

  return {
    ...baseAction,
    type: (config.type || defaultType) as any,
    onClick: config.onClick!,
  }
}

/**
 * Хук для настройки действий карточки
 * Автоматически разделяет действия на основную кнопку и вторичные действия
 */
export const useCardActions = ({ actions }: UseCardActionsProps): UseCardActionsReturn => {
  return useMemo(() => {
    const filteredActions = actions.filter(action => action.condition !== false)
    const sortedActions = filteredActions.sort((a, b) => (a.order || 0) - (b.order || 0))
    const primaryActionConfig = sortedActions.find(action => action.isPrimary)
    const secondaryActionConfigs = sortedActions.filter(action => !action.isPrimary)

    const primaryAction: AnyActionButton | undefined = primaryActionConfig
      ? createActionButton(primaryActionConfig, 'primary')
      : undefined

    const secondaryActions: AnyActionButton[] = secondaryActionConfigs.map(action =>
      createActionButton(action, 'default'),
    )

    return {
      primaryAction,
      secondaryActions,
    }
  }, [actions])
}
